/**
 * Settings -> Integrations -> sending calls somewhere else.
 *
 * Its own tab, following Inbound and Working hours, because it is the third
 * question in the same run and not a variation on either: Inbound answers "what
 * happens when somebody rings", Hours answers "and what if they ring at three in
 * the morning", and this one answers "and what if nobody is here at all".
 *
 * The card is deliberately in two halves, and they save differently:
 *
 *  - **Now.** The manual switch, thrown here exactly as the call log throws it,
 *    through its own endpoint and taking effect on the next call. No Save
 *    button, because "my calls go to my mobile from this moment" is not a form.
 *  - **The rules.** The number, the automatic night rule, how long the mobile
 *    rings and which caller id it shows. These are decided once and saved like
 *    any other settings form.
 *
 * The split is also what keeps the two screens from fighting: the form never
 * sends `enabled`, so saving a rule cannot silently switch forwarding on or off
 * behind somebody who threw the switch from the call log a minute ago.
 */
import { useCallback, useEffect, useState } from "react";
import { PhoneForwarded, Power, RefreshCw, Save } from "lucide-react";
import { DateTimePicker } from "@wa/ui";
import { useI18n } from "../../../../i18n/I18nProvider";
import {
  getCallDivert,
  setCallDivert,
  type CallDivertSchedule,
  type CallDivertState,
} from "../../../../lib/callDivert";
import { saveTelephonySettings } from "../../../../lib/calls";
import { formatPhone } from "../../../../lib/phone";
import { Btn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, FieldGroup, Note, Pill, SaveRow, ToggleRow } from "../panel/fields";
import { DivertScheduleFields } from "./DivertScheduleFields";
import { defaultWeek } from "./hoursWeek";

/**
 * The server's refusals, each with a sentence of its own.
 *
 * It refuses rather than saving something that would forward nothing, so every
 * one of these is a state somebody would otherwise leave the office believing
 * they had covered.
 */
/** What the form holds before the server has answered. Nothing ticked: this
 *  rule says when calls LEAVE, and a guessed week would be somebody else's
 *  opinion about when this office is empty. */
const EMPTY_SCHEDULE: CallDivertSchedule = {
  enabled: false,
  timezone: "Europe/Tallinn",
  days: defaultWeek().map((day) => ({ ...day, open: false })),
  from: "",
  to: "",
};

const REFUSALS: Record<string, string> = {
  invalid_number: "settings.forwarding.err.number",
  no_target: "settings.forwarding.err.noTarget",
  self_target: "settings.forwarding.err.self",
  invalid_until: "settings.forwarding.err.until",
};

/** The expiry as a clock, in the reader's own locale. */
function atTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function DivertCard() {
  // The locale as well as the translator: the date picker writes its own label
  // and needs to know which week starts on Monday.
  const { t, locale } = useI18n();
  const [state, setState] = useState<CallDivertState | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const [to, setTo] = useState("");
  const [afterHours, setAfterHours] = useState(false);
  const [ringSeconds, setRingSeconds] = useState("20");
  const [showCaller, setShowCaller] = useState(false);
  const [until, setUntil] = useState("");
  const [schedule, setSchedule] = useState<CallDivertSchedule>(EMPTY_SCHEDULE);

  const hydrate = useCallback((data: CallDivertState) => {
    setState(data);
    setTo(data.to);
    setAfterHours(data.afterHours);
    setRingSeconds(String(data.ringSeconds));
    setShowCaller(data.showCaller);
    setUntil(data.until);
    setSchedule({ ...data.schedule, days: data.schedule.days.map((day) => ({ ...day })) });
  }, []);

  useEffect(() => {
    getCallDivert()
      .then((r) => hydrate(r.divert))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [hydrate]);

  /**
   * Throw the switch.
   *
   * The number on screen goes with it, so an operator who types a mobile and
   * presses the button once gets what they asked for rather than a saved field
   * and a switch still pointing at yesterday's number.
   */
  async function toggle(next: boolean, moment = until) {
    setSwitching(true);
    setError(null);
    try {
      const r = await setCallDivert({ enabled: next, to: to.trim(), until: next ? moment : "" });
      hydrate(r.divert);
    } catch (err) {
      // The server refuses rather than quietly forwarding nothing, so each
      // refusal has a sentence of its own - see `divert.routes.ts`.
      const code = (err as { body?: { error?: string } })?.body?.error ?? "";
      setError(REFUSALS[code] ?? "settings.forwarding.err.failed");
    } finally {
      setSwitching(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    setError(null);
    try {
      // `enabled` and `until` are deliberately absent: this form owns the rules,
      // not the moment.
      await saveTelephonySettings({
        inbound: {
          divert: { to, afterHours, ringSeconds: Number(ringSeconds), showCaller, schedule },
        },
      });
      // Re-read rather than trust the settings answer: whether a call arriving
      // now would be diverted depends on the clock and the schedule, and the
      // server is the only place that pair is evaluated.
      hydrate((await getCallDivert()).divert);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  const active = state?.active ?? false;
  const phone = formatPhone(state?.to ?? "");
  // The number was typed and the server would not keep it. Same guard the
  // Inbound card puts on its own forwarding field, and the same reason: a leg
  // that cannot be dialled is the only leg on a diverted call.
  const dropped = to.trim().length > 0 && (state?.to ?? "") === "";

  return (
    <IntegrationCard
      icon={PhoneForwarded}
      title={t("settings.forwarding.title")}
      desc={t("settings.forwarding.desc")}
      status={
        <ConnStatus
          ok={active}
          okLabel={t("settings.forwarding.statusOn")}
          offLabel={t("settings.forwarding.statusOff")}
        />
      }
    >
      <div className="space-y-5">
        <Note tone="info">{t("settings.forwarding.about")}</Note>
        {/* Nothing here reaches a caller while the number is not answered at
            all: an unanswered line refuses the call before any of this is
            consulted. */}
        {!state?.inboundReady ? <Note tone="bad">{t("settings.forwarding.needsInbound")}</Note> : null}

        <FieldGroup title={t("settings.forwarding.nowGroup")}>
          {/* What is HAPPENING, not which switches are pressed. The two come
              apart on an expiry that has passed, a cleared number, or the night
              rule firing on a Sunday, and the second reading is the useless
              one. */}
          <Note tone={active ? "ok" : "mut"}>
            {!active
              ? t("settings.forwarding.inactive")
              : state?.reason === "after-hours"
                ? t("settings.forwarding.activeAfterHours", { phone })
                : state?.reason === "schedule"
                  ? t("settings.forwarding.activeSchedule", { phone })
                  : state?.until
                    ? t("settings.forwarding.activeUntil", { phone, time: atTime(state.until) })
                    : t("settings.forwarding.activeManual", { phone })}
          </Note>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("settings.forwarding.to")} hint={t("settings.forwarding.toHint")}>
              <Pill
                value={to}
                onChange={setTo}
                placeholder="+372..."
                autoComplete="off"
                inputMode="tel"
                mono
              />
            </Field>
            <Field
              label={t("settings.forwarding.durationLabel")}
              hint={t("settings.forwarding.durationHint")}
            >
              {/* Cleared is a real answer and the one most people want: "until I
                  switch it off". A moment picked while forwarding is already
                  running is written immediately - there is no Save button on
                  this half of the card, and an expiry that sat on screen
                  unsaved would be exactly the promise this field exists to
                  keep. */}
              <DateTimePicker
                value={until}
                locale={locale}
                placeholder={t("settings.forwarding.durationNone")}
                onChange={(next) => {
                  setUntil(next);
                  if (state?.enabled) void toggle(true, next);
                }}
              />
            </Field>
          </div>

          {dropped ? <Note tone="warn">{t("settings.forwarding.dropped")}</Note> : null}
          {state?.self ? <Note tone="bad">{t("settings.forwarding.self")}</Note> : null}
          {error ? <Note tone="bad">{t(error)}</Note> : null}

          <SaveRow>
            <Btn
              tone={state?.enabled ? "danger" : "dark"}
              disabled={switching}
              onClick={() => void toggle(!state?.enabled)}
            >
              {switching ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Power className="size-3.5" />
              )}
              {state?.enabled ? t("settings.forwarding.turnOff") : t("settings.forwarding.turnOn")}
            </Btn>
          </SaveRow>
        </FieldGroup>

        <form onSubmit={handleSave} className="space-y-5">
          <FieldGroup title={t("settings.forwarding.rulesGroup")}>
            <ToggleRow
              label={t("settings.forwarding.afterHours")}
              hint={t("settings.forwarding.afterHoursHint")}
              checked={afterHours}
              onChange={setAfterHours}
            />
            {/* The rule fires on a schedule that is not switched on, so it
                describes no moment at all. Worth saying here: the tab that
                owns the schedule is the one above this in the menu. */}
            {afterHours && !state?.hoursEnabled ? (
              <Note tone="warn">{t("settings.forwarding.needsHours")}</Note>
            ) : null}
            {/* What a night caller stops hearing. Somebody who wrote that
                announcement should be told it is now unreachable. */}
            {afterHours && state?.hoursEnabled ? (
              <Note tone="mut">{t("settings.forwarding.replacesVoicemail")}</Note>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={t("settings.forwarding.ringSeconds")}
                hint={t("settings.forwarding.ringSecondsHint")}
              >
                <Pill value={ringSeconds} onChange={setRingSeconds} inputMode="numeric" placeholder="20" />
              </Field>
            </div>

            <ToggleRow
              label={t("settings.forwarding.showCaller")}
              hint={t("settings.forwarding.showCallerHint")}
              checked={showCaller}
              onChange={setShowCaller}
            />
            {/* The failure this default exists for is silent at both ends: the
                mobile never rings, and the caller hears our ringback until the
                timeout. */}
            {showCaller ? <Note tone="warn">{t("settings.forwarding.showCallerRisk")}</Note> : null}
          </FieldGroup>

          {/* The rule the two switches above cannot express: a lunch hour, a
              closed Friday afternoon, a week in July. */}
          <DivertScheduleFields value={schedule} onChange={setSchedule} hasTarget={to.trim() !== ""} />

          <SaveRow>
            <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
              {saveState === "saving" ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              {saveState === "saving" ? t("settings.forwarding.saving") : t("settings.forwarding.save")}
            </Btn>
            {saveState === "saved" ? <StatusPill tone="ok">{t("settings.forwarding.saved")}</StatusPill> : null}
            {saveState === "error" ? (
              <StatusPill tone="lt">{t("settings.forwarding.saveError")}</StatusPill>
            ) : null}
          </SaveRow>
        </form>
      </div>
    </IntegrationCard>
  );
}
